import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BookOpen } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States';
import { PageHeader } from '@/components/layout/PageHeader';
import { ROUTES } from '@/app/router/routes';
import { useAuth } from '@/features/auth/useAuth';
import { courseworkPortalApi } from '@/services/api/endpoints/portal';
import { queryKeys } from '@/services/api/queryKeys';
import { PERMISSIONS } from '@/types/permissions';
import type { MyAssignment } from '@/types/portal';
import { formatDate, formatDateTime, humanise } from '@/utils/format';
import { SectionTitle, SubmissionBadge } from './shared';

const PAGE_SIZE = 20;

/**
 * Set work. A student reads their own (GET /assignments/mine, paged); a parent
 * reads every child's, grouped by child (GET /assignments/children, paged per
 * child). Both already exist in the coursework module — the portal reuses them.
 */
export function PortalAssignmentsPage() {
  const { can } = useAuth();

  if (can(PERMISSIONS.STUDENT_SELF_VIEW)) {
    return <StudentAssignments />;
  }
  if (can(PERMISSIONS.STUDENT_CHILD_VIEW)) {
    return <ChildrenAssignments />;
  }
  return <Navigate to={ROUTES.forbidden} replace />;
}

function Header() {
  return (
    <PageHeader
      title="Assignments"
      description="Homework and projects, with where each hand-in stands."
      icon={<BookOpen className="h-5 w-5" aria-hidden="true" />}
    />
  );
}

function Pager({
  page,
  hasNext,
  onChange,
}: {
  page: number;
  hasNext: boolean;
  onChange: (page: number) => void;
}) {
  if (page === 0 && !hasNext) {
    return null;
  }
  return (
    <div className="mt-4 flex items-center justify-between gap-3">
      <Button variant="secondary" size="sm" disabled={page === 0} onClick={() => onChange(page - 1)}>
        Newer
      </Button>
      <span className="text-xs text-muted">Page {page + 1}</span>
      <Button variant="secondary" size="sm" disabled={!hasNext} onClick={() => onChange(page + 1)}>
        Older
      </Button>
    </div>
  );
}

function StudentAssignments() {
  const [page, setPage] = useState(0);
  const params = { page, size: PAGE_SIZE };

  const mine = useQuery({
    queryKey: queryKeys.portal.myAssignments(params),
    queryFn: () => courseworkPortalApi.mine(params),
  });

  return (
    <div>
      <Header />
      {mine.isPending ? (
        <Card>
          <LoadingState label="Loading assignments" />
        </Card>
      ) : mine.isError ? (
        <Card>
          <ErrorState error={mine.error} onRetry={() => void mine.refetch()} />
        </Card>
      ) : mine.data.data.length === 0 ? (
        <Card>
          <EmptyState
            icon={<BookOpen className="h-6 w-6" aria-hidden="true" />}
            title="No assignments"
            description="Work your teachers set will appear here."
          />
        </Card>
      ) : (
        <>
          <Card>
            <AssignmentList items={mine.data.data} />
          </Card>
          <Pager page={page} hasNext={!mine.data.pagination.last} onChange={setPage} />
        </>
      )}
    </div>
  );
}

function ChildrenAssignments() {
  const [page, setPage] = useState(0);
  const params = { page, size: PAGE_SIZE };

  const children = useQuery({
    queryKey: queryKeys.portal.childAssignments(params),
    queryFn: () => courseworkPortalApi.children(params),
  });

  return (
    <div>
      <Header />
      {children.isPending ? (
        <Card>
          <LoadingState label="Loading assignments" />
        </Card>
      ) : children.isError ? (
        <Card>
          <ErrorState error={children.error} onRetry={() => void children.refetch()} />
        </Card>
      ) : children.data.length === 0 ? (
        <Card>
          <EmptyState
            icon={<BookOpen className="h-6 w-6" aria-hidden="true" />}
            title="No children linked"
            description="Your account is not linked to any student yet."
          />
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {children.data.map((child) => (
              <Card key={child.studentId}>
                <SectionTitle
                  action={
                    <span className="text-xs text-muted">
                      {child.totalAssignments} in total
                    </span>
                  }
                >
                  {child.studentName ?? 'Child'}
                </SectionTitle>
                {child.assignments.length === 0 ? (
                  <p className="px-4 py-6 text-sm text-muted">
                    {page === 0 ? 'No assignments.' : 'Nothing further on this page.'}
                  </p>
                ) : (
                  <AssignmentList items={child.assignments} />
                )}
              </Card>
            ))}
          </div>
          <Pager
            page={page}
            // Paged per child: another page exists while any child has more set work than has been shown.
            hasNext={children.data.some((child) => child.totalAssignments > (page + 1) * PAGE_SIZE)}
            onChange={setPage}
          />
        </>
      )}
    </div>
  );
}

function AssignmentList({ items }: { items: MyAssignment[] }) {
  return (
    <ul className="divide-y divide-line">
      {items.map((item) => (
        <li key={item.assignmentId} className="px-4 py-3">
          <div className="flex flex-wrap items-start gap-3">
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-ink">{item.title}</span>
              <span className="block text-[11px] text-subtle">
                {[item.subjectName, item.teacherName, humanise(item.assignmentType)]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
              <span
                className={
                  item.overdue
                    ? 'block text-[11px] font-semibold text-danger'
                    : 'block text-[11px] text-subtle'
                }
              >
                Due {formatDate(item.dueDate)}
                {item.overdue && ' · overdue'}
                {item.maxMarks !== undefined && ` · out of ${item.maxMarks}`}
              </span>
            </span>
            <SubmissionBadge status={item.submissionStatus} />
          </div>
          {item.description !== undefined && (
            <p className="mt-2 whitespace-pre-line break-words text-sm text-ink">{item.description}</p>
          )}
          {(item.attachments ?? []).length > 0 && (
            <p className="mt-1.5 text-[11px] text-muted">
              Attached: {(item.attachments ?? []).map((file) => file.fileName).join(', ')}
            </p>
          )}
          {item.submittedAt !== undefined && (
            <p className="mt-1.5 text-[11px] text-muted">
              Handed in {formatDateTime(item.submittedAt)}
              {item.late && ' · late'}
            </p>
          )}
          {item.teacherFeedback !== undefined && (
            <p className="mt-2 border-l-2 border-accent bg-surface-2 px-3 py-2 text-xs text-ink">
              <span className="font-semibold">Teacher’s feedback: </span>
              {item.teacherFeedback}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}
